import { Link } from 'react-router-dom'
import { EstadoVazio } from '../../components/Estados'
import { IconeCheque } from '../../components/Icones'
import { TEXTOS } from '../../lib/glossario'
import type { ConformidadeDeGmud as Conformidade, GmudNoRelatorio } from '../../types/relatorios'
import { dataDoInstante } from '../iad/pontuacao'

/**
 * A conformidade das mudanças do mês — a Regra nº 1 virando documento.
 *
 * Responde uma pergunta só: **o que foi a produção neste mês passou pelo comitê?** Cada linha diz se
 * houve aprovação, quando, por quem, e se a execução saiu da janela.
 *
 * **O histórico importado não entra.** As 1.027 solicitações do sistema anterior nunca passaram por
 * comitê no sentido do contrato; contá-las como não aprovadas produziria mil inconformidades falsas
 * num documento que a fiscalização assina.
 *
 * @param props o relatório do mês
 * @returns os totais e a lista
 */
export function ConformidadeDeGmud({ conformidade }: { conformidade: Conformidade }) {
  const t = TEXTOS.relatorios.conformidade

  if (conformidade.gmuds.length === 0) {
    return <EstadoVazio icone={<IconeCheque className="h-9 w-9" />} titulo={t.semMovimento.titulo} ajuda={t.semMovimento.ajuda} />
  }

  return (
    <div className="space-y-4">
      <dl className="grid gap-3 sm:grid-cols-3">
        <Total rotulo={t.semAprovacao} valor={conformidade.semAprovacao} alerta />
        <Total rotulo={t.foraDaJanela} valor={conformidade.foraDaJanela} alerta={conformidade.foraDaJanela > 0} />
        <Total rotulo={t.emergenciais} valor={conformidade.emergenciais} />
      </dl>

      <ul className="divide-y divide-slate-100 rounded-lg ring-1 ring-slate-200">
        {conformidade.gmuds.map((gmud) => (
          <Linha key={gmud.codigo} gmud={gmud} />
        ))}
      </ul>
    </div>
  )
}

/** Um número que a fiscalização olha antes de abrir a lista. */
function Total({ rotulo, valor, alerta }: { rotulo: string; valor: number; alerta?: boolean }) {
  return (
    <div className={alerta && valor > 0 ? 'cartao bg-amber-50 p-3' : 'cartao p-3'}>
      <dt className="text-xs text-slate-600">{rotulo}</dt>
      <dd className="text-2xl font-bold tabular-nums text-slate-900">{valor}</dd>
    </div>
  )
}

/** Uma solicitação: passou pelo comitê ou não, e o que mais o contrato quer saber. */
function Linha({ gmud }: { gmud: GmudNoRelatorio }) {
  const t = TEXTOS.relatorios.conformidade
  return (
    <li className="space-y-1.5 px-3 py-3 text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <div className="min-w-0">
          <span className="font-medium text-slate-900">{gmud.codigo}</span>{' '}
          <span className="text-slate-600">{gmud.sistema}</span>
          {gmud.chamadoNumero && <span className="text-slate-500"> · {gmud.chamadoNumero}</span>}
        </div>
        {gmud.aprovadaEm ? (
          <span className="whitespace-nowrap rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-900">
            {t.aprovada}
          </span>
        ) : (
          <span className="whitespace-nowrap rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-900">
            {t.semAprovacaoNaTrilha}
          </span>
        )}
      </div>

      {gmud.aprovadaEm && (
        <p className="text-xs text-slate-600">
          {t.aprovadaPor(gmud.aprovadaPorNome ?? '', dataDoInstante(gmud.aprovadaEm))}
        </p>
      )}
      {gmud.aprovacaoForaDaAplicacao && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">{t.aprovacaoForaDaAplicacao}</p>
      )}
      {gmud.implantadaEm && (
        <p className="text-xs text-slate-600">{t.implantadaEm(dataDoInstante(gmud.implantadaEm))}</p>
      )}
      {gmud.foraDaJanela && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {t.executouForaDaJanela} {gmud.foraDaJanelaJustificativa ?? t.semJustificativa}
        </p>
      )}
      {gmud.prioridade === 'EMERGENCIAL' && (
        <p className="text-xs text-slate-600">{t.emergencial(gmud.emergencialCausa ?? '')}</p>
      )}

      <Link to={`/gmuds?q=${encodeURIComponent(gmud.codigo)}`} className="text-xs text-sky-800 hover:underline">
        {t.abrir}
      </Link>
    </li>
  )
}
